import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import type { TooltipContentProps } from 'recharts'
import type { DailyRevenueItem } from '../api/admin-api'

interface DailyRevenueChartProps {
  data: DailyRevenueItem[]
}

function formatDateLabel(dateStr: string): string {
  const date = new Date(dateStr)
  const dayNames = ['일', '월', '화', '수', '목', '금', '토']
  return `${date.getMonth() + 1}/${date.getDate()} (${dayNames[date.getDay()]})`
}

function formatKrw(num: number): string {
  return num.toLocaleString('ko-KR')
}

function CustomTooltip({ active, payload, label }: Partial<TooltipContentProps<number, string>>) {
  if (!active || !payload?.length) return null

  const data = payload[0]?.payload as DailyRevenueItem | undefined
  if (!data) return null

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <p className="text-sm font-medium text-gray-900 mb-2">
        {formatDateLabel(String(label))}
      </p>
      <div className="space-y-1">
        <p className="text-sm text-blue-600">
          매출: <span className="font-semibold">{formatKrw(data.revenueKrw)}</span>원
        </p>
        <p className="text-sm text-rose-500">
          원가: <span className="font-semibold">{formatKrw(data.costKrw)}</span>원
        </p>
        <p className="text-sm text-teal-500">
          차익: <span className="font-semibold">{formatKrw(data.profitKrw)}</span>원
        </p>
      </div>
    </div>
  )
}

export function DailyRevenueChart({ data }: DailyRevenueChartProps) {
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
        <XAxis
          dataKey="date"
          tickFormatter={(value) => {
            const d = new Date(value)
            return `${d.getMonth() + 1}/${d.getDate()}`
          }}
          tick={{ fontSize: 11, fill: '#64748B' }}
          axisLine={{ stroke: '#E2E8F0' }}
          tickLine={false}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#64748B' }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(value) => formatKrw(value)}
          width={55}
        />
        <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F1F5F9' }} />
        <Legend
          verticalAlign="top"
          height={30}
          formatter={(value: string) => {
            const labels: Record<string, string> = { costKrw: '원가', profitKrw: '차익' }
            return labels[value] || value
          }}
        />
        <Bar dataKey="costKrw" stackId="revenue" fill="#F43F5E" radius={[0, 0, 0, 0]} maxBarSize={32} />
        <Bar dataKey="profitKrw" stackId="revenue" fill="#14B8A6" radius={[4, 4, 0, 0]} maxBarSize={32} />
      </BarChart>
    </ResponsiveContainer>
  )
}
